import React from 'react';
import { styled } from '@mui/material/styles';
import {
  CompanyListCompanyDto,
} from 'models/dtoTypes';
import generateNonce from 'utils/generateNonce';
import { CompanyListItemAttributes } from 'kosmos/models';
import BulkEditableCompanyList from './_bulkEditableCompanyList';
import LinearProgress from '@mui/material/LinearProgress';
import {
  CreateOrUpdateCompanyListItem,
  DeleteCompanyListItem,
  GetCompanyListItems,
} from 'services/companyList';
import CsvUploadDialog from 'kosmos/dialogs/CsvUploadDialog';
import { Box, Pagination } from '@mui/material';
import SampleCsvLink from './_sampleCsvLink';
import CreateResourceModal, { ISchema } from 'kosmos/dialogs/CreateResourceModal';

const PREFIX = 'CompanyListEditTable';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  }
}));

type CompanyListItemsWithCount = {
  pageCount?: number;
  count?: number;
  page?: number;
  items?: CompanyListCompanyDto[] | null;
};

interface ICountryOption {
  label: string;
  alpha3: string;
}

interface IProps {
  countries: ICountryOption[];
  companyListId: string;
  onErrorResponse: (e: unknown) => void;
}

export default function CompanyListEditTable(props: IProps) {
  const {
    onErrorResponse,
    companyListId,
    countries,
  } = props;

  const [openAddNewItem, setOpenAddNewItem] = React.useState(false);
  const [openCsvUploader, setOpenCsvUploader] = React.useState(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(1);
  const [nonce, setNonce] = React.useState('');
  const [result, setResult] = React.useState<CompanyListItemsWithCount>();

  const generateSchema = (): ISchema => ({
    fields: [
      {
        label: 'Name',
        name: 'name',
        required: false,
        widget: 'text',
        description: 'The official name for this supplier'
      },
      {
        label: 'Country',
        name: 'countryCode',
        required: false,
        widget: 'select',
        options: countries.map(o => ({
          alias: o.alpha3 || '',
          label: o.label,
        })),
        description: 'Country',
      },
      {
        label: 'Company Number',
        name: 'registrationNumbers',
        required: false,
        widget: 'text',
        description: 'The company number for this supplier'
      },
    ]
  });

  const rows = result?.items || [];

  const loadItems = async (pageIndex: number) => {
    setLoading(true);
    const list = await GetCompanyListItems(companyListId, { pageIndex });
    setResult(list);
    setLoading(false);
  };

  React.useEffect(() => {
    void loadItems(page - 1);
  }, [nonce, page]);

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleDelete = async (row: CompanyListItemAttributes) => {
    if (!companyListId) {
      return;
    }
    if (row.id) {
      try {
        await DeleteCompanyListItem(companyListId, row.id);
      } catch (e) {
        onErrorResponse(e);
      }
    }
  };

  const handleCheckCanEdit = (row: CompanyListItemAttributes) => {
    const currentItem = rows.find(r => r.id === row.id);
    return currentItem?.verificationStatus !== 'Verified';
  };

  const handleCreateOrUpdateItem = async (row: CompanyListItemAttributes) => {
    if (!companyListId) {
      return;
    }
    try {
      const res = await CreateOrUpdateCompanyListItem(companyListId, row);
      return res;
    } catch (e) {
      onErrorResponse(e);
    }
  };

  const handleFinish = () => {
    setOpenCsvUploader(false);
    setNonce(generateNonce());
  };

  const handleAddNew = async (data: CompanyListItemAttributes) => {
    await handleCreateOrUpdateItem(data);
    setOpenAddNewItem(false);
    setNonce(generateNonce());
  };

  return (
    <Root>
      {loading && <LinearProgress />}
      {!loading && (
        <BulkEditableCompanyList<CompanyListItemAttributes>
          countries={countries}
          list={rows}
          onCanEdit={handleCheckCanEdit}
          onAdd={() => setOpenAddNewItem(true)}
          onCsvUpload={() => setOpenCsvUploader(true)}
          onDelete={handleDelete}
          onSubmit={handleCreateOrUpdateItem}
        />
      )}
      {result && (
        <div className={classes.pagination}>
          <Pagination
            count={result.pageCount}
            page={page}
            onChange={handleChangePage}
          />
        </div>
      )}
      {companyListId && (
        <>
          <CsvUploadDialog
            companyListId={companyListId}
            visible={openCsvUploader}
            onClose={() => setOpenCsvUploader(false)}
            onFinish={handleFinish}
          />
          {!!countries?.length && (
            <CreateResourceModal<CompanyListItemAttributes>
              open={openAddNewItem}
              label="Add a new supplier"
              schema={generateSchema()}
              onClose={() => setOpenAddNewItem(false)}
              onSave={handleAddNew}
            />
          )}
        </>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Box sx={{ flex: '1 1 auto' }} />
        <SampleCsvLink />
      </Box>
    </Root>
  );
}
